import React, { Fragment, useEffect, useMemo, useState, useCallback } from 'react';
import classNames from 'classnames';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { 
  AdminListAndEditLayout,
  EditorHeader,
  ListHeaderWrapper,
  ListContentWrapper,
  TableHeader,
  editorTitleByView
} from '../../../reactcomponents/Admin';
import { TableIconicButton } from '../../../reactcomponents/Form';
import { I } from '../../../common/v5/config';
import { ADMIN_TITLE, FORM_CREATEGROUP } from '../../../common/v5/constants';
import { ActionsWrapper, Edit as ActionEdit, Delete as ActionDelete } from '../../../reactcomponents/Admin';
import { useCallbackWithValue } from '../../../hooks/callback';
import { SingleSelectDD } from '../../../reactcomponents/Dropdown';
import { ModalBox } from '../../../reactcomponents/Modal';
import AgentEditForm from './agentEdit';
import AgentUploadForm from './agentUploadForm';

// Custom SearchBar component for real agent search
const AgentSearchBar = ({onSearch, className, placeholder, icon, currentFilter, onSearchResults, onSearchTextChange, onClearSearch}) => {
  const [searchText, setSearchText] = useState('');
  
  // Notify parent when search text changes
  useEffect(() => {
    if (onSearchTextChange) {
      onSearchTextChange(searchText);
    }
  }, [searchText, onSearchTextChange]);
  
  const handleSearch = () => {
    if (onSearch && typeof onSearch === 'function') {
      try {
        onSearch(searchText, currentFilter).then((results) => {
          if (onSearchResults) {
            onSearchResults(results, searchText);
          }
        }).catch((error) => {
          console.error('Search error:', error);
        });
      } catch (error) {
        console.error('Error during agent search:', error);
      }
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.keyCode === 13) { // Enter key
      handleSearch();
    }
  };
  
  const handleClear = () => {
    setSearchText('');
    if (onSearchResults) {
      onSearchResults(null, ''); // Clear results
    }
    if (onClearSearch) {
      onClearSearch(); 
    }
  };
  
  return (
    <div className={className}>
      <i className={icon}></i>
      <input
        placeholder={placeholder}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyDown={handleKeyDown}
        type="text"
      />
      {searchText && (
        <button 
          className="search-clear-btn" 
          onClick={handleClear}
          title={I("Clear search")}
        >
          <i className="fa fa-times"></i>
        </button>
      )}
      <button 
        className="search-submit-btn" 
        onClick={handleSearch}
        title={I("Search")}
      >
        <i className="fa fa-search"></i>
      </button>
    </div>
  );
};

const FORM_EDIT = 'edit';
const FORM_UPLOAD = 'upload';

const AgentsBase = ({
  view,
  onLoad,
  showInline,
  list,
  onLoadToEdit,
  onChangeAdminInput,
  onSaveAdmin,
  onDeleteFromList,
  onCloseForm,
  activeId,
  input,
  formType,
  onLoadToUpload,
  onFetchAdminList,
  adminStatus,
  areaList,
  availableAreas,
  langSrc,
  timeZone,
  salutationList,
  signatureList,
  // Group-related props
  groupsAgentList,
  groupAgentListReady,
  onGetAgentGroups,
  onSaveAgentGroups,
  // Filter-related props
  onFilterChange,
  // Admin status monitoring
  currentAdminStatus,
  // Agent activation/deactivation
  onActivateAgent,
  // Agent search functionality
  onSearchAgents,
  // Agent upload functionality
  onUploadAgents,
  ...props
}) => {
  
  useEffect(() => {
    if (onLoad) {
      onLoad();
    }
  }, [onLoad]);

  // Monitor admin status to refresh list after save completion
  const [lastSaveStatus, setLastSaveStatus] = useState(null);
  
  // State for search and filter functionality
  const [filterBy, setFilterBy] = useState(0); // Default to "All" filter
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentSearchText, setCurrentSearchText] = useState('');
  const [isInSearchMode, setIsInSearchMode] = useState(false);
  
  // State for upload form
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    if (currentAdminStatus && currentAdminStatus.status === 2 && lastSaveStatus !== 2) {
      // Save completed successfully - refresh the list
      console.log('Agent save completed successfully, refreshing list...');
      setTimeout(() => {
        onFetchAdminList();
      }, 500);
      setLastSaveStatus(2);
    } else if (currentAdminStatus && currentAdminStatus.status === 1) {
      // Save started
      setLastSaveStatus(1);
    } else if (currentAdminStatus && currentAdminStatus.status === 3) {
      // Save failed
      console.log('Agent save failed:', currentAdminStatus.msg);
      setLastSaveStatus(3);
    }
  }, [currentAdminStatus, lastSaveStatus, onFetchAdminList]);

  // Note: Initial load is handled by onLoad() in the container
  // No need for additional API calls in useEffect


  // Nested filter structure with Status and Level groups
  const agentFilter = [
    { Id: 0, Name: I('Status'),
      Areas: [
        {Id: 0, Name: I('Active')},
        {Id: 1, Name: I('Inactive')},
        {Id: 2, Name: I('Both')}
      ]
    },
    { Id: 1, Name: I('Level'),
      Areas: [
        {Id: 3, Name: I('Agent')},
        {Id: 4, Name: I('Observer')},
        {Id: 5, Name: I('Administrator')},
        {Id: 6, Name: I('All')}
      ]
    }
  ];

  // Field mapping for nested dropdown
  const keyAreas = {key: 'Areas'};
  const idName = {id: "Id", name: "Name"};



  // CSS is now loaded via webpack imports - no dynamic loading needed

  // Helper function to get display text for selected filter
  const getFilterDisplayText = (selectedValue) => {
    if (!selectedValue && selectedValue !== 0) {
      return I("Filter");
    }
    
    // Search through nested structure to find the display name
    for (const group of agentFilter) {
      for (const item of group.Areas) {
        if (item.Id === selectedValue) {
          return item.Name;
        }
      }
    }
    
    return I("Filter");
  };

  // Filter handler
  const handleFilter = (selectedFilter) => {
    setFilterBy(selectedFilter);
    
    // Set search mode when user changes filter
    setIsInSearchMode(true);
    
    // Clear search results when filter changes to show full list with new filter
    setSearchResults(null);
    
    // Always update the list with the new filter
    if (onSearchAgents && typeof onSearchAgents === 'function') {
      try {
        // If we have search text, search with that text and new filter
        if (currentSearchText.trim()) {
          onSearchAgents(currentSearchText, selectedFilter).then((results) => {
            setSearchResults(results);
          }).catch((error) => {
            console.error('Error during filter search:', error);
          });
        } else {
          // If no search text, just load the filtered list
          onSearchAgents('', selectedFilter).then((results) => {
            if (results && Array.isArray(results)) {
              setSearchResults(results);
            }
          }).catch((error) => {
            console.error('Error during filter load:', error);
          });
        }
      } catch (error) {
        console.error('Error during filter handling:', error);
      }
    }
  };

  const handleDelete = (id) => {
    let confirmDelMsg = I('Are you sure you want to delete this agent?');
    if (confirm(confirmDelMsg)) {
      onDeleteFromList(id);
    }
  };

  // Upload form handlers
  const handleUploadClick = () => {
    setShowUploadForm(true);
  };

  const handleUploadClose = () => {
    setShowUploadForm(false);
  };

  const handleUploadSuccess = (formData) => {    
    if (onUploadAgents && formData) {
      onUploadAgents(formData);
    }
    setShowUploadForm(false);
    
    // Refresh the list after processing
    setTimeout(() => {
      if (onFetchAdminList) {
        onFetchAdminList();
      }
    }, 3000);
  };

  const handleDownloadTemplate = () => {
    const exportFilename = 'agent_list.csv';
    const csv = 'Login_Name,Password,Admin_Level,User_Name,Email_Address,External_ID,Bright_Reference_Code,Signature_Name,Chat_Name\n';
    const csvData = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(csvData, exportFilename);
    } else {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(csvData);
      link.setAttribute('download', exportFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };


  // Wrapper components for actions with proper ID handling
  const EditAction = useCallback(({ id }) => (
    <ActionEdit onClick={useCallbackWithValue(id, onLoadToEdit)} />
  ), [onLoadToEdit]);

  const DeleteAction = useCallback(({ id }) => (
    <ActionDelete onClick={useCallbackWithValue(id, handleDelete)} />
  ), [handleDelete]);

  const renderActionButtons = (cell, row) => {
    return (
      <ActionsWrapper>
        <EditAction id={row.id} />
        <DeleteAction id={row.id} />
      </ActionsWrapper>
    );
  };

  // Determine which data to display - search results or regular list
  const displayData = searchResults || list || [];
  

  const columns = useMemo(() => [
    {
      dataField: 'id',
      text: 'ID',
      sort: true,
      hidden: true
    },
    {
      dataField: 'name',
      text: I('Name'),
      sort: true,
      headerStyle: { width: '200px' }
    },
    {
      dataField: 'groups',
      text: I('Groups'),
      sort: true,
      headerStyle: { width: '100px' },
      formatter: (cell, row) => {
        const groupCount = row.groupCount || 0;
        const groupNames = row.groupNames || [];
        
        if (groupCount === 0) {
          return <span className="text-muted">0</span>;
        }
        
        const groupCountBadge = (
          <span className="badge badge-pill badge-secondary" style={{cursor: 'pointer'}}>
            {groupCount}
          </span>
        );
        
        if (!groupNames || groupNames.length === 0) {
          return groupCountBadge;
        }
        
        return (
          <div 
            title={groupNames.join(', ')} 
            className="group-count-tooltip"
            style={{cursor: 'help'}}
          >
            {groupCountBadge}
          </div>
        );
      }
    },
    {
      dataField: 'active',
      text: I('Account Status'),
      sort: true,
      headerStyle: { width: '120px' },
      formatter: (cell, row) => {
        const isActive = cell || row.status;
        return (
          <span className={`agent-status ${isActive ? 'active' : 'deactivated'}`}>
            {isActive ? (
              <><i className="fas fa-check-circle text-success" aria-label="Active"></i> {I('Active')}</>
            ) : (
              <><i className="fas fa-times-circle text-danger" aria-label="Deactivated"></i> {I('Deactivated')}</>
            )}
          </span>
        );
      }
    },
    {
      dataField: 'locked',
      text: I('Locked'),
      sort: true,
      headerStyle: { width: '80px' },
      formatter: (cell, row) => (
        <span className="agent-locked-status">
          {cell ? (
            <><i className="fas fa-lock" aria-label="Locked"></i> {I('Yes')}</>
          ) : (
            I('No')
          )}
        </span>
      )
    },
    {
      isDummyField: true,
      dataField: 'action',
      text: I('Action'),
      headerClasses: 'center',
      headerStyle: { width: '120px' },
      formatter: (cell, row) => {
        return renderActionButtons(cell, row);
      }
    }
  ], [onLoadToEdit, handleDelete]);

  const defaultSorted = [{
    dataField: 'name',
    order: 'asc'
  }];

  const isNew = activeId <= 0;
  const isCreate = formType === FORM_EDIT && !activeId;
  const isUpload = formType === FORM_UPLOAD;

  const options = {
    paginationSize: 3,
    pageStartIndex: 1,
    firstPageText: '<<',
    prePageText: '<',
    nextPageText: '>',
    lastPageText: '>>',
    nextPageTitle: 'First page',
    prePageTitle: 'Pre page',
    firstPageTitle: 'Next page',
    lastPageTitle: 'Last page',
    showTotal: true,
    sizePerPageList: [10]
  };

  let listSection = <Fragment>
    <ToolkitProvider
      keyField="id"
      data={displayData}
      columns={columns}
    >
      {
        props => (
          <>
            <ListHeaderWrapper>
              <div className="agents-page agents-header-section">
                {/* First Row: Title and Action Buttons */}
                <div className="agents-title-row">
                  <div className="agents-title-section">
                    <h3 className="agents-page-title">{I('Agents')}</h3>
                  </div>
                  <div className="action-buttons-group">
                    <button
                      type='button'
                      className="btn btn-primary add-agent-btn"
                      onClick={e => onLoadToEdit(0)}
                      title={I('Add new agent')}
                    >
                      <i className="fa fa-plus"></i>
                    </button>
                    <button
                      type='button'
                      className={classNames("btn btn-secondary upload-btn", { "active": showUploadForm })}
                      onClick={handleUploadClick}
                      title={I('Upload agents')}
                    >
                      <i className="fa fa-upload"></i>
                    </button>
                  </div>
                </div>
                
                {/* Second Row: Search and Filter */}
                <div className="agents-search-filter-row">
                  <div className="search-section-full">
                    <AgentSearchBar
                      onSearch={onSearchAgents}
                      className="search-field-text"
                      placeholder={I("Search agent")}
                      icon="icon-search"
                      currentFilter={filterBy}
                      onSearchResults={(results, searchText) => {
                        setSearchResults(results);
                        setIsInSearchMode(true);
                        console.log('searchResults state after set:', results);
                      }}
                      onSearchTextChange={setCurrentSearchText}
                      onClearSearch={() => {
                        setSearchResults(null);
                        setIsInSearchMode(false);
                      }}
                    />
                    {searchResults && isInSearchMode && (
                      <div className="search-results-info">
                        <small className="text-muted">
                          {I("Showing")} {searchResults.length} {I("search results")}
                          <button 
                            className="btn btn-link btn-sm p-0 ml-2"
                            onClick={() => {
                              setSearchResults(null);
                              setIsInSearchMode(false);
                            }}
                            title={I("Clear search")}
                          >
                            {I("Show all")}
                          </button>
                        </small>
                      </div>
                    )}
                  </div>
                  <div className="filter-section-right">
                    <span className="agentfilterBox">
                      <SingleSelectDD
                        id="agent-status-level-filter"
                        nested={keyAreas}
                        data={agentFilter}
                        idFields={idName}
                        selected={filterBy}
                        show={showFilterDropdown}
                        onToggle={() => setShowFilterDropdown(!showFilterDropdown)}
                        onSelect={(value) => {
                          handleFilter(value);
                          setShowFilterDropdown(false); // Close dropdown after selection
                        }}
                        textNoItemSelected={I("Filter")}
                      >
                        <button 
                          type="button"
                          className="btn btn-default dropdown-toggle filter-btn"
                          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                        >
                          <i className="fa fa-filter"></i> {I("Filter")} 
                        </button>
                      </SingleSelectDD>
                    </span>
                  </div>
                </div>
              </div>
            </ListHeaderWrapper>
            <ListContentWrapper className={"v5-table-style"}>
              <BootstrapTable
                keyField="id"
                data={displayData}
                noDataIndication={I("No data to display")}
                columns={columns}
                bordered={false}
                condensed={false}
                defaultSorted={defaultSorted}
                pagination={paginationFactory(options)}
                hover
                {...props.baseProps}
              />
            </ListContentWrapper>
          </>
        )
      }
    </ToolkitProvider>
  </Fragment>;

  let editSection = <Fragment>
    {showInline ? (
      <>
        {formType === FORM_EDIT &&
          <AgentEditForm
            key={`agent-edit-${activeId}`}
            activeId={activeId}
            input={input}
            view={view}
            onChangeAdminInput={onChangeAdminInput}
            onSave={onSaveAdmin}
            onCancel={onCloseForm}
            onDelete={handleDelete}
            isNew={isNew}
            hidden={!showInline}
            adminStatus={adminStatus}
            areaList={areaList}
            availableAreas={availableAreas}
            langSrc={langSrc}
            timeZone={timeZone}
            salutationList={salutationList}
            signatureList={signatureList}
            // Group-related props
            groupsAgentList={groupsAgentList}
            groupAgentListReady={groupAgentListReady}
            onGetAgentGroups={onGetAgentGroups}
            onSaveAgentGroups={onSaveAgentGroups}
            // Access key props
            accessKeyData={props.accessKeyData}
            accessKeyURLCreator={props.accessKeyURLCreator}
            onCreateAgentAccessKey={props.onCreateAgentAccessKey}
            onDeleteAgentAccessKey={props.onDeleteAgentAccessKey}
            onLoadAgentAccessKeys={props.onLoadAgentAccessKeys}
            timezoneOffset={0}
            // Skills props
            availableSkills={props.availableSkills}
            proficiencyList={props.proficiencyList}
            onLoadSkills={props.onLoadSkills}
            onSaveAgentSkill={props.onSaveAgentSkill}
            onRemoveAgentSkill={props.onRemoveAgentSkill}
            onUpdateAgentSkill={props.onUpdateAgentSkill}
            onActivateAgent={onActivateAgent}
            onSearchAgents={onSearchAgents}
            {...props}
          />
        }
      </>
    ) : (
      // Placeholder content when not editing
      <div className="edit-placeholder">
        <div className="placeholder-icon">
          <i className="icon-user"></i>
        </div>
        <div className="placeholder-title">
          {I('Edit Agent')}
        </div>
        <div className="placeholder-text">
          {I('Select an agent from the list to view and edit their details. You can also create new agents or manage existing ones.')}
        </div>
      </div>
    )}
  </Fragment>;

  // Upload form modal
  const uploadFormModal = showUploadForm && (
    <ModalBox
      show={showUploadForm}
      onClose={handleUploadClose}
      className="modal-agent-upload"
      headerContent={<h3>{I("Upload Agent List")}</h3>}
    >
      <AgentUploadForm
        onClose={handleUploadClose}
        onUpload={handleUploadSuccess}
        onDownloadTemplate={handleDownloadTemplate}
      />
    </ModalBox>
  );

  // Add data-editing attribute when form is active
  const isEditing = showInline && (formType === FORM_EDIT || formType === FORM_UPLOAD);
  
  useEffect(() => {
    const adminContainer = document.querySelector('.agents-page');
    const adminAgentsContainer = document.querySelector('.AdminAgents');
    const anyContainer = document.querySelector('[class*="agents"]');
    
    // Try multiple selectors to ensure the attribute is set
    const containers = [adminContainer, adminAgentsContainer, anyContainer].filter(Boolean);
    
    containers.forEach((container) => {
      if (isEditing) {
        container.setAttribute('data-editing', 'true');
      } else {
        container.removeAttribute('data-editing');
      }
    });
  }, [isEditing, showInline, formType]);

  return (
    <>
      <AdminListAndEditLayout
        idClass={"AdminAgents"}
        className={"agents-page"}
        listSection={listSection}
        editSection={editSection}
      />
      {uploadFormModal}
    </>
  );
};

export default AgentsBase; 